// drawOla cloud sync: the local / cloud / pending / error icon. Fork-only.
//
// Rendered by the Studio cards and the editor breadcrumb, both outside the
// `<Excalidraw>` tree, so it translates through `useStudioT`.
//
// Why the tooltip is a portal
// ---------------------------
// The Studio cards and list rows are `overflow: hidden`, so a tooltip
// positioned inside them is clipped away. Same problem, same answer as
// `studio/StudioCardMenu.tsx`: render into `document.body` through a portal,
// repeat the `.excalidraw` (+ `.theme--dark`) classes so the design tokens
// resolve, and position `fixed` from the trigger's viewport rect. A fixed
// element cannot follow its trigger, so scrolling and resizing close it.

import { THEME } from "@excalidraw/common";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import type { Theme } from "@excalidraw/element/types";

import { useStudioT } from "../../studio/i18n";

import type { SyncStatus } from "./CloudSyncEngine";

/** vertical gap between the trigger and the tooltip, px */
const TIP_GAP = 6;
/** keep the tooltip at least this far from every viewport edge, px */
const VIEWPORT_MARGIN = 8;

type Props = {
  status: SyncStatus;
  /** Display name of the bound provider; empty for a local-only draw. */
  providerName: string;
  /**
   * Where a local-only draw would be backed up, for the local tooltip. The
   * caller reads it off the engine, so no provider name is hardcoded here.
   */
  defaultProviderName?: string;
  /** Current app theme, so the tooltip portal matches the rest of the UI. */
  theme: Theme;
  /** Opens the repair panel; only used for the error state. */
  onRepair?: () => void;
};

type TipPosition = { top: number; left: number };

const LOCAL_ICON = (
  <svg
    className="drawola-sync__local"
    viewBox="0 0 20 20"
    width="13"
    height="13"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="14" height="9" rx="1.5" />
    <path d="M7 16h6" />
  </svg>
);

const CLOUD_ICON = (
  <svg
    className="drawola-sync__cloud"
    viewBox="0 0 20 20"
    width="13"
    height="13"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinejoin="round"
  >
    <path d="M6 15a3.5 3.5 0 0 1-.5-6.97 4.5 4.5 0 0 1 8.95.6A3 3 0 0 1 15 15H6Z" />
  </svg>
);

const BANG_ICON = (
  <svg
    className="drawola-sync__bang"
    viewBox="0 0 20 20"
    width="13"
    height="13"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M10 5v6" />
    <path d="M10 14.5v.01" />
  </svg>
);

const clamp = (value: number, min: number, max: number) =>
  // `max` can fall below `min` when the tooltip is bigger than the viewport;
  // the near edge wins in that case.
  Math.min(Math.max(value, min), Math.max(min, max));

/** Accessible name and tooltip heading for one status. */
function titleFor(
  status: SyncStatus,
  providerName: string,
  t: ReturnType<typeof useStudioT>,
): string {
  switch (status.kind) {
    case "local":
      return t("cloud.sync.statusLocal");
    case "synced":
      return t("cloud.sync.statusSynced", { provider: providerName });
    case "pending":
      return t("cloud.sync.statusPending", { provider: providerName });
    case "error":
      return t("cloud.sync.statusError", { provider: providerName });
  }
}

/** One sentence saying what the state means and what to do about it. */
function bodyFor(
  status: SyncStatus,
  providerName: string,
  defaultProviderName: string | undefined,
  t: ReturnType<typeof useStudioT>,
): string {
  switch (status.kind) {
    case "local":
      // A local draw has no binding, so `providerName` is empty; name the
      // provider it would be saved to instead. With cloud storage off both
      // are empty and the sentence still reads.
      return t("cloud.sync.tipLocal", {
        provider: defaultProviderName ?? providerName,
      });
    case "synced":
      return t("cloud.sync.tipSynced", { provider: providerName });
    case "pending":
      return t("cloud.sync.tipPending", { provider: providerName });
    case "error":
      return t("cloud.sync.tipError", { provider: providerName });
  }
}

/** Local icon always; cloud icon when bound; a dot while pending; a bang on error. */
export const SyncStatusIcon: React.FC<Props> = ({
  status,
  providerName,
  defaultProviderName,
  theme,
  onRepair,
}) => {
  const t = useStudioT();
  const tipId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TipPosition | null>(null);

  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
  }, []);

  // Measure + place once per open. Until `position` lands the tooltip renders
  // hidden at the origin, which is what gives us its natural size to measure;
  // the state update happens inside a layout effect, so it is flushed before
  // the browser paints and nothing flashes at 0,0.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const triggerRect = trigger.getBoundingClientRect();
    const tipRect = tipRef.current?.getBoundingClientRect();
    const tipWidth = tipRect?.width ?? 0;
    const tipHeight = tipRect?.height ?? 0;
    const below = triggerRect.bottom + TIP_GAP;
    const flip = below + tipHeight + VIEWPORT_MARGIN > window.innerHeight;
    setPosition({
      top: clamp(
        flip ? triggerRect.top - TIP_GAP - tipHeight : below,
        VIEWPORT_MARGIN,
        window.innerHeight - tipHeight - VIEWPORT_MARGIN,
      ),
      left: clamp(
        triggerRect.left,
        VIEWPORT_MARGIN,
        window.innerWidth - tipWidth - VIEWPORT_MARGIN,
      ),
    });
  }, [open]);

  // A fixed tooltip cannot follow its trigger, so don't try: close instead.
  // `scroll` does not bubble, hence the capture-phase listener on `window`.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const title = titleFor(status, providerName, t);
  const body = bodyFor(status, providerName, defaultProviderName, t);
  const className = clsx("drawola-sync", `drawola-sync--${status.kind}`);

  const triggerProps = {
    className,
    "aria-label": title,
    "aria-describedby": open ? tipId : undefined,
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    },
  };

  const tip =
    open &&
    createPortal(
      <div
        className={clsx("excalidraw", "drawola-studio-portal", {
          "theme--dark": theme === THEME.DARK,
        })}
      >
        <div
          ref={tipRef}
          id={tipId}
          role="tooltip"
          className="drawola-sync-tip"
          style={{
            top: position ? `${position.top}px` : 0,
            left: position ? `${position.left}px` : 0,
            visibility: position ? undefined : "hidden",
          }}
        >
          <strong className="drawola-sync-tip__title">{title}</strong>
          <p className="drawola-sync-tip__body">{body}</p>
        </div>
      </div>,
      document.body,
    );

  if (status.kind === "error") {
    return (
      <>
        <button
          ref={setTriggerRef}
          type="button"
          {...triggerProps}
          onClick={onRepair}
        >
          {LOCAL_ICON}
          {CLOUD_ICON}
          {BANG_ICON}
        </button>
        {tip}
      </>
    );
  }
  return (
    <>
      {/* `tabIndex` so keyboard users can reach the tooltip; the error state
          is a button and already focusable. */}
      <span ref={setTriggerRef} role="img" tabIndex={0} {...triggerProps}>
        {LOCAL_ICON}
        {status.kind !== "local" && CLOUD_ICON}
        {status.kind === "pending" && <span className="drawola-sync__dot" />}
      </span>
      {tip}
    </>
  );
};
